// ============================================================================
// DuplicateBoundaries ViewModel
// ============================================================================

import { useState, useCallback } from "react";
import type { DuplicateBoundariesProps, DuplicateBoundariesViewModel } from "./DuplicateBoundaries.types";
import type { MergeConflict, MergeResolutions } from "../../../../../../../utilities/mergeJobs.types";
import { detectMergeConflicts } from "../../../../../../../utilities/mergeJobs";
import { getJobDisplayName } from "../../shared";

/**
 * ViewModel hook for DuplicateBoundaries component
 */
export function useDuplicateBoundariesViewModel(props: DuplicateBoundariesProps): DuplicateBoundariesViewModel {
	const { issues, currentIssueIndex, skippedIssueIds, onDeleteJob, onMergeJobs } = props;

	// Track which jobs are hidden on the map
	const [hiddenJobIds, setHiddenJobIds] = useState<Set<string>>(new Set());
	// State for delete confirmation dialog
	const [deleteJobId, setDeleteJobId] = useState<string | null>(null);
	// State for merge confirmation dialog
	const [showMergeConfirm, setShowMergeConfirm] = useState(false);
	// State for merge conflict modal
	const [mergeConflicts, setMergeConflicts] = useState<MergeConflict[] | null>(null);

	// Derived state
	const hasIssues = issues.length > 0;
	const currentIssue = hasIssues ? (issues[currentIssueIndex] ?? null) : null;
	const isSkipped = currentIssue ? skippedIssueIds.has(currentIssue.id) : false;

	// Feature type text - always "boundary" for this step
	const featureTypeText = "boundary";

	// Duplicate count
	const duplicateCount = currentIssue?.duplicateCount ?? 0;

	// Merge availability
	const canMerge = (currentIssue?.jobs.length ?? 0) >= 2;

	// Toggle job visibility
	const toggleJobVisibility = useCallback((jobId: string) => {
		setHiddenJobIds((prev) => {
			const next = new Set(prev);
			if (next.has(jobId)) {
				next.delete(jobId);
			} else {
				next.add(jobId);
			}
			return next;
		});
	}, []);

	// Show all jobs
	const showAllJobs = useCallback(() => {
		setHiddenJobIds(new Set());
	}, []);

	// Hide all jobs
	const hideAllJobs = useCallback(() => {
		if (currentIssue) {
			setHiddenJobIds(new Set(currentIssue.jobs.map((j) => j.id)));
		}
	}, [currentIssue]);

	// Get job display name from jobs in current issue
	const getJobDisplayNameForId = useCallback(
		(jobId: string): string => {
			const job = currentIssue?.jobs.find((j) => j.id === jobId);
			if (!job) return "this job";
			return getJobDisplayName(job);
		},
		[currentIssue],
	);

	// Get source file names for a job (filename only, not full path)
	const getJobSourceFiles = useCallback(
		(jobId: string): string[] => {
			if (!currentIssue?.jobFilePaths) return [];
			const filePaths = currentIssue.jobFilePaths[jobId] || [];
			// Extract filename from path
			return filePaths.map((path) => {
				const parts = path.split(/[/\\]/);
				return parts[parts.length - 1] || path;
			});
		},
		[currentIssue],
	);

	// Handle delete job with confirmation
	const handleDeleteJobClick = useCallback((jobId: string) => {
		setDeleteJobId(jobId);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (deleteJobId) {
			onDeleteJob(deleteJobId);
		}
		setDeleteJobId(null);
	}, [deleteJobId, onDeleteJob]);

	const handleCancelDelete = useCallback(() => {
		setDeleteJobId(null);
	}, []);

	// Handle merge button click - opens confirmation dialog
	const handleMergeClick = useCallback(() => {
		if (!canMerge) return;
		setShowMergeConfirm(true);
	}, [canMerge]);

	// Confirm merge from the confirmation dialog - runs conflict detection
	const handleMergeConfirmDialog = useCallback(() => {
		setShowMergeConfirm(false);
		if (!currentIssue) return;

		const conflicts = detectMergeConflicts(currentIssue.jobs);
		if (conflicts.length === 0) {
			// No conflicts - merge directly
			onMergeJobs(currentIssue.jobIds);
		} else {
			// Has conflicts - open conflict resolution modal
			setMergeConflicts(conflicts);
		}
	}, [currentIssue, onMergeJobs]);

	// Cancel the merge confirmation dialog
	const handleMergeCancelDialog = useCallback(() => {
		setShowMergeConfirm(false);
	}, []);

	// Confirm merge with resolutions from the conflict modal
	const handleMergeConfirm = useCallback(
		(resolutions: MergeResolutions) => {
			if (!currentIssue) return;
			onMergeJobs(currentIssue.jobIds, resolutions);
			setMergeConflicts(null);
		},
		[currentIssue, onMergeJobs],
	);

	// Cancel the merge conflict modal
	const handleMergeCancel = useCallback(() => {
		setMergeConflicts(null);
	}, []);

	return {
		hasIssues,
		currentIssue,
		isSkipped,
		featureTypeText,
		duplicateCount,
		hiddenJobIds,
		toggleJobVisibility,
		showAllJobs,
		hideAllJobs,
		getJobDisplayName: getJobDisplayNameForId,
		getJobSourceFiles,
		deleteJobId,
		handleDeleteJobClick,
		handleConfirmDelete,
		handleCancelDelete,
		canMerge,
		showMergeConfirm,
		mergeConflicts,
		handleMergeClick,
		handleMergeConfirmDialog,
		handleMergeCancelDialog,
		handleMergeConfirm,
		handleMergeCancel,
	};
}
